import { Button, Drawer, FormField, Input } from '@ntconsult/aluma-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckProfessionals } from '../hooks/useCheckProfessionals';
import type {
  UnallocatedAdvancedValues,
  UnallocatedFilterState,
} from '../hooks/useUnallocatedFilters';
import type { CheckProfessionalOption, FilterOption } from '../types/Checks';
import { professionalLabel } from '../utils/labels';
import { MultiSearchCombobox } from './MultiSearchCombobox';
import { MultiSelectFilter } from './MultiSelectFilter';

const TITLE_ID = 'unallocated-advanced-filters-title';
const FORM_ID = 'unallocated-advanced-filters';

type UnallocatedFiltersDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterState: UnallocatedFilterState;
  filterOptions: { contractTypes: FilterOption[]; contractStatuses: FilterOption[] } | undefined;
};

function draftFrom({ filters, professionals }: UnallocatedFilterState): UnallocatedAdvancedValues {
  return {
    professionals,
    profileSearch: filters.profileSearch ?? '',
    contractTypeIds: filters.contractTypeIds ?? [],
    contractStatusIds: filters.contractStatusIds ?? [],
  };
}

const toProfessionalOption = (professional: CheckProfessionalOption) => ({
  id: professional.id,
  name: professional.name.trim(),
});

/** No molde de `AdvancedFiltersDrawer`; Profissionais são seleção exata (a coluna busca por texto). */
export function UnallocatedFiltersDrawer({
  open,
  onClose,
  filterState,
  filterOptions,
}: UnallocatedFiltersDrawerProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const [draft, setDraft] = useState(() => draftFrom(filterState));
  const [wasOpen, setWasOpen] = useState(open);

  // Reinicia o rascunho a partir do que está aplicado sempre que o painel abre.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(draftFrom(filterState));
  }

  const date = filterState.filters.date;
  // O `MultiSearchCombobox` chama este hook no próprio render; só a `date` varia.
  const useProfessionalsOnDate = useCallback(
    // biome-ignore lint/correctness/useHookAtTopLevel: ver comentário acima
    (search: string) => useCheckProfessionals('unallocated', search, date),
    [date],
  );

  const apply = () => {
    filterState.applyAdvanced(draft);
    onClose();
  };

  const multiSelect = (
    key: 'contractTypeIds' | 'contractStatusIds',
    options: FilterOption[] | undefined,
  ) => (
    <MultiSelectFilter
      options={(options ?? []).map((option) => ({ id: option.id, label: option.name }))}
      value={draft[key]}
      onChange={(ids) => setDraft((d) => ({ ...d, [key]: ids }))}
    />
  );

  return (
    <Drawer open={open} onClose={onClose} size="md" ariaLabelledBy={TITLE_ID}>
      <Drawer.Header
        titleId={TITLE_ID}
        dismissLabel={t('common:close')}
        title={t('filtros.avancados')}
        subtitle={t('consultas.filtros.avancadosDescricao')}
        onClose={onClose}
      />
      <Drawer.Body>
        <form
          id={FORM_ID}
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <FormField label={t('consultas.colunas.profissional')}>
            <MultiSearchCombobox
              label={t('consultas.colunas.profissional')}
              placeholder={t('filtros.profissionaisPlaceholder')}
              searchPlaceholder={t('consultas.filtros.nomeOuMatriculaPlaceholder')}
              emptyLabel={t('filtros.semResultados')}
              removeChipLabel={(name) => t('filtros.chips.remover', { filter: name })}
              value={draft.professionals}
              onChange={(professionals) => setDraft((d) => ({ ...d, professionals }))}
              useSearch={useProfessionalsOnDate}
              toOption={toProfessionalOption}
              getItemLabel={professionalLabel}
            />
          </FormField>
          <FormField label={t('consultas.colunas.tipo')}>
            {multiSelect('contractTypeIds', filterOptions?.contractTypes)}
          </FormField>
          <FormField label={t('consultas.colunas.perfil')}>
            <Input
              value={draft.profileSearch}
              onChange={(e) => setDraft((d) => ({ ...d, profileSearch: e.target.value }))}
            />
          </FormField>
          <FormField label={t('consultas.colunas.situacao')}>
            {multiSelect('contractStatusIds', filterOptions?.contractStatuses)}
          </FormField>
        </form>
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('filtros.cancelar')}
        </Button>
        <Button type="submit" form={FORM_ID}>
          {t('filtros.aplicar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
